/**
 * OneParacon design tokens — the single source of truth for the visual system.
 *
 * Extracted from the approved brand guideline sheets (v1.2):
 *   §02 Colour Palette · §03 Typography Hierarchy · §11 Status Labels
 *
 * `tailwind.config.js` consumes this file, so correcting a value here updates
 * every utility class across the app. Never hard-code a hex value in a component.
 */

/** §02 — Core brand colours */
export const brand = {
  /** Primary brand. Headings, logo. */
  ink: '#24313C',
  /** Sidebar, navigation, primary buttons, auth panel. The workhorse. */
  navy: '#173248',
  /** Dark surfaces, hover states on navy. */
  midnight: '#102634',
  /** Links, secondary actions, informational accents. */
  blue: '#3D6B8C',
  /** Success, approvals, positive actions, avatars. */
  green: '#2D8769',
  /** Warnings, pending, expiring. */
  gold: '#D6A93A',
  /**
   * Support functions on the organisational chart — marketing, admin,
   * technology, accounts. Taken from the client's chart design, and used for
   * nothing that carries status meaning.
   */
  violet: '#7A4FC9',
} as const;

/** §02 — Neutral colours */
export const neutral = {
  /** Application background. */
  cloud: '#F4F7F9',
  white: '#FFFFFF',
  /** Dividers, table headers, subtle fills. */
  paleBlue: '#E4EAF2',
  /** Primary body text. */
  text: '#0A2A3C',
  /** Labels, helper text, meta. */
  textSecondary: '#6C757E',
  /** Card, input and table borders. */
  border: '#D9E1E3',
} as const;

/**
 * Semantic colours for status and feedback.
 * Each status carries a background, foreground and border so pills, banners and
 * dots all derive from one definition.
 */
export const semantic = {
  success: { base: '#2D8769', fg: '#1F6B52', bg: '#E7F4EF', border: '#C3E4D7' },
  warning: { base: '#D6A93A', fg: '#9A741B', bg: '#FBF3E0', border: '#F0DFB4' },
  danger: { base: '#C0453F', fg: '#A2332D', bg: '#FBEAE9', border: '#F1C9C6' },
  info: { base: '#3D6B8C', fg: '#2F5470', bg: '#E9F0F6', border: '#C8DAE8' },
  neutral: { base: '#6C757E', fg: '#4F585F', bg: '#F1F4F6', border: '#DCE3E7' },
} as const;

/**
 * Project colours — offered when a project is set up. The chosen colour becomes
 * the app's accent (active navigation, tabs, the top bar strip) while that
 * project is selected; see lib/accent.ts. Picked to sit beside Structural Navy
 * and stay legible as a tint. `accentDefault` is the accent with no project
 * colour, and matches the approved design.
 */
export const accentDefault = '#2D8769';

export const projectPalette = [
  { name: 'Signal Green', hex: '#2D8769' },
  { name: 'Digital Blue', hex: '#3D6B8C' },
  { name: 'Harbour', hex: '#1F7A99' },
  { name: 'Teal', hex: '#13837A' },
  { name: 'Violet', hex: '#7A4FC9' },
  { name: 'Plum', hex: '#9B3F7A' },
  { name: 'Brick', hex: '#C0453F' },
  { name: 'Terracotta', hex: '#C2622D' },
  { name: 'Ochre', hex: '#B7861F' },
  { name: 'Slate', hex: '#556575' },
] as const;

/**
 * §03 — Typography hierarchy.
 * Tuples of [fontSize, { lineHeight, letterSpacing, fontWeight }].
 */
export const typography = {
  display: ['2.125rem', { lineHeight: '1.15', letterSpacing: '-0.02em', fontWeight: '700' }],
  pageTitle: ['1.5rem', { lineHeight: '1.25', letterSpacing: '-0.015em', fontWeight: '700' }],
  sectionTitle: ['1.125rem', { lineHeight: '1.35', letterSpacing: '-0.01em', fontWeight: '600' }],
  cardTitle: ['0.9375rem', { lineHeight: '1.4', letterSpacing: '-0.005em', fontWeight: '600' }],
  body: ['0.8125rem', { lineHeight: '1.55', fontWeight: '400' }],
  tableCell: ['0.8125rem', { lineHeight: '1.45', fontWeight: '400' }],
  label: ['0.6875rem', { lineHeight: '1.35', letterSpacing: '0.04em', fontWeight: '600' }],
  helper: ['0.6875rem', { lineHeight: '1.5', fontWeight: '400' }],
  kpi: ['1.75rem', { lineHeight: '1.1', letterSpacing: '-0.02em', fontWeight: '700' }],
} as const;

/** Layout constants shared between the shell and its content. */
export const layout = {
  /** Collapsed icon rail width. */
  railWidth: 72,
  /** Expanded sidebar width. */
  sidebarWidth: 288,
  /** Flyout panel width. */
  flyoutWidth: 282,
  /** Top bar height. */
  topbarHeight: 72,
  /** Recent-activity rail on the person detail page. */
  activityRailWidth: 470,
  /** Auth split-panel left side. */
  authPanelWidth: 600,
} as const;

export const radius = {
  sm: '0.25rem',
  DEFAULT: '0.5rem',
  md: '0.5rem',
  lg: '0.625rem',
  xl: '0.75rem',
  '2xl': '1rem',
  full: '9999px',
} as const;

/** Soft, low-contrast elevation — the design uses shadow sparingly. */
export const elevation = {
  card: '0 1px 2px 0 rgb(16 38 52 / 0.04), 0 1px 3px 0 rgb(16 38 52 / 0.03)',
  raised: '0 2px 4px -1px rgb(16 38 52 / 0.06), 0 4px 12px -2px rgb(16 38 52 / 0.06)',
  flyout: '0 8px 24px -4px rgb(16 38 52 / 0.24), 0 2px 8px -2px rgb(16 38 52 / 0.16)',
  modal: '0 20px 48px -12px rgb(16 38 52 / 0.28)',
} as const;
